import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { apiClient } from '../utils/apiClient';
import MenuBar from './MenuBar';
import Footer from './Footer';
import { PaymentForm } from './PaymentForm';
import { OrderTotal } from './OrderTotal';
import { License, PriceCheck } from './declarations/license';
import { CustomDialog } from './CustomDialog';
import { ErrorDialog, paymentErrors, requestErrors, RequestErrorMessage } from './declarations/errors';

interface InvoiceFormValues {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  invoiceNumber: string;
  otherInformation: string;
  paymentAmount: string;
}

const defaultValues: InvoiceFormValues = {
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  invoiceNumber: '',
  otherInformation: '',
  paymentAmount: '0.00',
};

const parseAmount = (value?: string): number => {
  if (!value) {
    return 0;
  }
  const sanitized = value.replace(/[^0-9.]/g, '');
  const parsed = parseFloat(sanitized);
  if (Number.isFinite(parsed)) {
    return Math.max(0, Math.round(parsed * 100) / 100);
  }
  return 0;
};

function Invoice(): JSX.Element {
  const [priceCheck, setPriceCheck] = useState<PriceCheck>({ price: 0, fullPrice: 0 });
  const [errorDialog, setErrorDialog] = useState<ErrorDialog>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    setValue,
  } = useForm<InvoiceFormValues>({ defaultValues });

  const paymentAmountValue = watch('paymentAmount');

  const licenses = useMemo<License[]>(() => [], []);

  useEffect(() => {
    const parsedAmount = parseAmount(paymentAmountValue);
    setPriceCheck((prev) => {
      if (prev.price === parsedAmount && prev.fullPrice === parsedAmount) {
        return prev;
      }
      return {
        price: parsedAmount,
        fullPrice: parsedAmount,
      };
    });
  }, [paymentAmountValue]);

  const handlePaymentBlur = useCallback(
    (value?: string) => {
      const amount = parseAmount(value);
      setValue('paymentAmount', amount.toFixed(2), { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const navigateToSuccess = useCallback(
    (formData: InvoiceFormValues, paymentMethod: 'PayPal' | 'Credit card') => {
      const name = `${formData.firstName} ${formData.lastName}`.trim();
      const invoiceNumber = formData.invoiceNumber?.trim();
      const company = formData.company?.trim();
      const email = formData.email?.trim();
      const otherInformation = formData.otherInformation?.trim();
      const formattedAmount = parseAmount(formData.paymentAmount).toFixed(2);

      navigate('/success-invoice', {
        state: {
          paymentInfo: {
            name: name || undefined,
            company: company || undefined,
            email: email || undefined,
            amount: formattedAmount,
            invoiceNumber: invoiceNumber || undefined,
            otherInformation: otherInformation || undefined,
            paymentAmount: formattedAmount,
            paymentMethod,
            currency: 'USD',
          },
        },
      });
    },
    [navigate]
  );

  const handlePaymentError = useCallback(() => {
    setErrorDialog({ ...paymentErrors['Payment Error'] });
    setIsSubmitting(false);
  }, []);

  const handleClick = useCallback(() => {
    setIsSubmitting(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  const updatePaymentIntentAmount = useCallback(
    async (amount: number) => {
      if (amount <= 0) {
        throw new Error('Amount must be greater than zero');
      }

      try {
        await apiClient.post('/update_payment_amount', {
          amount,
        });
      } catch (error: any) {
        const networkMessage = error?.message as RequestErrorMessage;
        const dialog =
          (networkMessage && requestErrors[networkMessage]) || {
            title: 'Payment Error',
            message: error?.response?.data?.error || 'Unable to update payment amount. Please refresh and try again.',
          };
        setErrorDialog(dialog);
        throw error;
      }
    },
    [setErrorDialog]
  );

  const handleApprove = useCallback(
    (firstName: string, lastName: string, email: string) => {
      const formValues = getValues();

      if (!formValues.firstName && firstName) {
        setValue('firstName', firstName, { shouldDirty: true });
      }
      if (!formValues.lastName && lastName) {
        setValue('lastName', lastName, { shouldDirty: true });
      }
      if (!formValues.email && email) {
        setValue('email', email, { shouldDirty: true });
      }

      navigateToSuccess(getValues(), 'PayPal');
      setIsSubmitting(false);
    },
    [getValues, navigateToSuccess, setValue]
  );

  const onSubmitButton = useCallback(
    async (data: InvoiceFormValues) => {
      if (!stripe || !elements) {
        return;
      }

      setIsSubmitting(true);

      try {
        await updatePaymentIntentAmount(priceCheck.price);
      } catch {
        setIsSubmitting(false);
        return;
      }

      try {
        const payload = await stripe.confirmPayment({
          elements,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name: `${data.firstName} ${data.lastName}`.trim(),
                email: data.email,
              },
            },
          },
          redirect: 'if_required',
        });

        if (payload.error) {
          handlePaymentError();
          return;
        }

        navigateToSuccess(data, 'Credit card');
      } catch (error) {
        handlePaymentError();
      } finally {
        setIsSubmitting(false);
      }
    },
    [elements, handlePaymentError, navigateToSuccess, priceCheck.price, stripe, updatePaymentIntentAmount]
  );

  const stripeClientSecret = window.stripeClientSecret;
  if (!stripeClientSecret) {
    return (
      <Box sx={{ textAlign: 'center', mt: 5 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Payment System Unavailable
        </Typography>
        <Typography variant="body1">
          Unable to connect to payment system. Please refresh the page or try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <MenuBar />
      <Box
        sx={{
          py: 2,
          maxWidth: { xs: '95%', md: '85%', lg: '75%' },
          mx: 'auto',
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Invoice Payment
          </Typography>
          <Typography variant="body1">
            Enter your invoice and payment information here. We will confirm successful payment. If you have any
            questions or need any help, please contact us{' '}
            <a href="https://www.moderncsv.com/contact-us">here</a>.
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmitButton)}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '1.25fr 0.75fr',
              },
              gap: { xs: '20px', md: '40px' },
              mb: 12,
              '& > *:not(:last-child)': {
                borderRight: { xs: 'none', md: '1px solid rgba(0, 0, 0, 0.12)' },
                borderBottom: { xs: '1px solid rgba(0, 0, 0, 0.12)', md: 'none' },
                paddingRight: { xs: 0, md: '40px' },
                paddingBottom: { xs: '20px', md: 0 },
              },
            }}
          >
            <Box>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="firstName"
                      control={control}
                      rules={{ required: 'First name is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="First Name *"
                          fullWidth
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      name="lastName"
                      control={control}
                      rules={{ required: 'Last name is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Last Name *"
                          fullWidth
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                        />
                      )}
                    />
                  </Box>
                </Box>

                <Controller
                  name="company"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Company (Optional)"
                      fullWidth
                      error={!!errors.company}
                      helperText={errors.company?.message}
                    />
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Enter a valid email address',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email *"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />

                <Controller
                  name="invoiceNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Invoice Number (Optional)"
                      fullWidth
                      error={!!errors.invoiceNumber}
                      helperText={errors.invoiceNumber?.message}
                    />
                  )}
                />

                <Controller
                  name="otherInformation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Other Information (Optional)"
                      fullWidth
                      multiline
                      rows={4}
                      error={!!errors.otherInformation}
                      helperText={errors.otherInformation?.message}
                    />
                  )}
                />

                <Controller
                  name="paymentAmount"
                  control={control}
                  rules={{
                    required: 'Payment amount is required',
                    validate: (value) => parseAmount(value) > 0 || 'Payment amount must be greater than $0.00',
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Payment Amount *"
                      fullWidth
                      error={!!errors.paymentAmount}
                      helperText={errors.paymentAmount?.message}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      onBlur={(event) => {
                        field.onBlur();
                        handlePaymentBlur(event.target.value);
                      }}
                    />
                  )}
                />
              </Stack>
            </Box>

            <Box>
              <OrderTotal licenses={licenses} priceCheck={priceCheck} onChange={setPriceCheck} formType="invoice" />
              <PaymentForm
                licenses={licenses}
                priceCheck={priceCheck}
                onPriceChange={setPriceCheck}
                onClick={handleClick}
                onApprove={handleApprove}
                onError={handlePaymentError}
                onCancel={handleCancel}
                disableFreeMode
              />
            </Box>
          </Box>
        </form>
      </Box>
      <Footer />

      <Backdrop sx={{ color: 'white', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSubmitting}>
        <CircularProgress sx={{ color: 'blue' }} />
      </Backdrop>
      <CustomDialog opened={!!errorDialog} title={errorDialog?.title || ''} body={errorDialog?.message || ''} />
    </div>
  );
}

export default Invoice;
